/**
 * Created: 2026-10-15
 * Initial commit: Admin schedules coaching appointments from the slots page
 *
 * Dialog for an admin to create a coaching appointment on the court-hours
 * selected in the slot grid. Same flow and rules as the mini coach appoint
 * page (roster mode, course, invited students with headcounts), plus a coach
 * picker: the admin schedules on behalf of one of the site's coaches.
 *
 * Updated: 2026-10-19 - Offer every schedulable course, not only those of the
 * selected courts' sport
 * Updated: 2026-10-21 - `coachId` given (the coach calendar): a coach schedules
 * for themselves, with no coach picker and no `coachId` sent, as the mini
 * appoint page does
 * Updated: 2026-11-03 - Open enrollment offers only courses of at least 3
 * seats
 */

import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { message } from '@homemaster/jslib'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import {
  hoursCapOf,
  isEnrollable,
  maxHeadcountOf as maxHeadcountFor,
} from '@tc-app/shared/coaching/appointment'
import { useState } from 'react'
import {
  AdminCreateCoachingAppointmentDocument,
  AdminSchedulableCoursesDocument,
  AdminSchedulableStudentsDocument,
  AppointmentCoachesDocument,
  RosterMode,
  SiteRole,
} from '../gql/graphql'
import { getWeekdayText } from '../lib/dateUtils'

// The site's coaches, for the coach picker.
gql`
  query AppointmentCoaches($userFilter: UserFilterInput!) {
    users(userFilter: $userFilter) {
      id
      openid
      name
      mobile
    }
  }
`

gql`
  query AdminSchedulableCourses($siteId: UUID!, $date: Date!, $hours: Int!) {
    schedulableCourses(siteId: $siteId, date: $date, hours: $hours) {
      id
      name
      sport
      hours
      price
      seats
    }
  }
`

gql`
  query AdminSchedulableStudents($courseId: UUID!, $date: Date!, $hours: Int!) {
    schedulableStudents(courseId: $courseId, date: $date, hours: $hours) {
      student {
        id
        openid
        name
        mobile
      }
      availableHours
      validUntil
    }
  }
`

gql`
  mutation AdminCreateCoachingAppointment(
    $courseId: UUID!
    $rosterMode: RosterMode!
    $students: [CoachingStudentInput!]!
    $slots: [CoachingSlotInput!]!
    $coachId: UUID
  ) {
    createCoachingAppointment(
      courseId: $courseId
      rosterMode: $rosterMode
      students: $students
      slots: $slots
      coachId: $coachId
    ) {
      ... on CreateCoachingAppointmentResponse {
        sessionId
      }
      ... on GraphqlRequestError {
        code
        message
      }
    }
  }
`

export interface AppointmentSlot {
  courtId: string
  courtName: string
  hour: number
}

interface CreateAppointmentDialogProps {
  siteId: string
  /** yyyy-MM-dd */
  date: string
  /** Sorted by hour, one court per hour. */
  slots: AppointmentSlot[]
  /**
   * Set when a coach schedules for themselves: the coach is fixed and the
   * mutation goes without `coachId`, which only a site admin may pass.
   */
  coachId?: string
  onClose: () => void
  onSuccess: (sessionId: string) => void
}

const formatHour = (h: number) => `${h.toString().padStart(2, '0')}:00`

export default function CreateAppointmentDialog({
  siteId,
  date,
  slots,
  coachId: ownCoachId,
  onClose,
  onSuccess,
}: CreateAppointmentDialogProps) {
  const [rosterMode, setRosterMode] = useState<RosterMode | null>(null)
  const [pickedCoachId, setCoachId] = useState('')
  const coachId = ownCoachId ?? pickedCoachId
  const [courseId, setCourseId] = useState('')
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([])
  // People each selected student brings, themselves included; absent means 1.
  const [headcounts, setHeadcounts] = useState<Record<string, number>>({})

  const sessionHours = slots.length

  const { data: coachesData, loading: coachesLoading } = useQuery(
    AppointmentCoachesDocument,
    {
      variables: { userFilter: { siteId, roles: [SiteRole.Coach] } },
      skip: !!ownCoachId,
      fetchPolicy: 'network-only',
    },
  )
  const coaches = [...(coachesData?.users ?? [])].sort((a, b) =>
    (a.name ?? '').localeCompare(b.name ?? '', 'zh-CN'),
  )

  // Courses with students who can attend this session, listed or not: a
  // delisted course still has entitlements to honour.
  const { data: coursesData, loading: coursesLoading } = useQuery(
    AdminSchedulableCoursesDocument,
    {
      variables: { siteId, date, hours: sessionHours },
      fetchPolicy: 'network-only',
    },
  )
  // Any course can be held on any court, e.g. a beginners' tennis course on
  // a pickleball court.
  const allCourses = coursesData?.schedulableCourses ?? []
  // Open enrollment is for group courses only.
  const coursesFor = (mode: RosterMode | null) =>
    mode === RosterMode.Enroll
      ? allCourses.filter((c) => isEnrollable(c.seats))
      : allCourses
  const courses = coursesFor(rosterMode)
  const selectedCourse = courses.find((c) => c.id === courseId)

  const { data: studentsData, loading: studentsLoading } = useQuery(
    AdminSchedulableStudentsDocument,
    {
      variables: { courseId, date, hours: sessionHours },
      skip: !courseId || rosterMode !== RosterMode.Invite,
      fetchPolicy: 'network-only',
    },
  )
  const eligibleStudents = (studentsData?.schedulableStudents ?? []).flatMap((s) =>
    s.student.id
      ? [
          {
            userId: s.student.id,
            name: s.student.name ?? '未知',
            mobile: s.student.mobile,
            availableHours: s.availableHours,
            validUntil: s.validUntil,
          },
        ]
      : [],
  )

  const [scheduleSession, { loading: submitting }] = useMutation(
    AdminCreateCoachingAppointmentDocument,
  )

  const headcountOf = (studentId: string) => headcounts[studentId] ?? 1
  const sumHeadcounts = (ids: string[], counts: Record<string, number>) =>
    ids.reduce((sum, id) => sum + (counts[id] ?? 1), 0)
  const totalHeadcount = sumHeadcounts(selectedStudentIds, headcounts)
  const seats = selectedCourse?.seats ?? 1
  // A 1:1 course has no room for a companion, so no stepper at all.
  const showHeadcount = seats > 1
  // A student whose hours cover only themselves gets no stepper either.
  const canBringCompanion = (availableHours: number) =>
    hoursCapOf(availableHours, sessionHours) > 1
  const maxHeadcountOf = (studentId: string, availableHours: number) =>
    maxHeadcountFor({
      seats,
      totalHeadcount,
      ownHeadcount: headcountOf(studentId),
      availableHours,
      sessionHours,
    })

  const canSubmit =
    !!rosterMode &&
    !!coachId &&
    !!selectedCourse &&
    (rosterMode === RosterMode.Enroll ||
      (selectedStudentIds.length > 0 && totalHeadcount <= seats))

  const selectRosterMode = (mode: RosterMode) => {
    if (mode === rosterMode) return
    setRosterMode(mode)
    // The course picked for the other mode may not be offered in this one.
    if (!coursesFor(mode).some((c) => c.id === courseId)) setCourseId('')
    setSelectedStudentIds([])
    setHeadcounts({})
  }

  const selectCourse = (id: string) => {
    if (id === courseId) return
    setCourseId(id)
    // Students and headcounts chosen for another course.
    setSelectedStudentIds([])
    setHeadcounts({})
  }

  const toggleStudent = (studentId: string) => {
    if (selectedStudentIds.includes(studentId)) {
      setSelectedStudentIds(selectedStudentIds.filter((id) => id !== studentId))
      return
    }
    // 1:1: picking another student replaces the current one, like a radio.
    if (seats === 1) {
      setSelectedStudentIds([studentId])
      return
    }
    const next = [...selectedStudentIds, studentId]
    if (sumHeadcounts(next, headcounts) > seats) {
      message.error(`最多选择 ${seats} 人`)
      return
    }
    setSelectedStudentIds(next)
  }

  const handleSubmit = async () => {
    if (!canSubmit || !rosterMode) return
    try {
      const result = await scheduleSession({
        variables: {
          courseId,
          rosterMode,
          coachId: ownCoachId ? undefined : coachId,
          students:
            rosterMode === RosterMode.Invite
              ? selectedStudentIds.map((studentId) => ({
                  studentId,
                  headcount: headcountOf(studentId),
                }))
              : [],
          slots: slots.map((s) => ({ courtId: s.courtId, date, hour: s.hour })),
        },
      })
      const response = result.data?.createCoachingAppointment
      if (response?.__typename === 'GraphqlRequestError') {
        message.error(response.message || '创建失败')
        return
      }
      if (response?.__typename === 'CreateCoachingAppointmentResponse') {
        message.success('预约已创建')
        onSuccess(response.sessionId)
      }
    } catch (error) {
      console.error('Failed to schedule session:', error)
      message.error('创建失败')
    }
  }

  const firstHour = slots[0].hour
  const lastHour = slots[slots.length - 1].hour
  // Court names in hour order, each once: "1号场" or "1号场、2号场".
  const courtNames = [...new Set(slots.map((s) => s.courtName))].join('、')

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>创建课程预约</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              已选时段
            </Typography>
            <Typography>
              {courtNames} · {date} {getWeekdayText(date)} {formatHour(firstHour)}-
              {formatHour(lastHour + 1)}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              1. 选择预约方式
            </Typography>
            <RadioGroup
              row
              value={rosterMode ?? ''}
              onChange={(e) => selectRosterMode(e.target.value as RosterMode)}
            >
              <FormControlLabel
                value={RosterMode.Invite}
                control={<Radio />}
                label="邀请指定学员"
              />
              <FormControlLabel
                value={RosterMode.Enroll}
                control={<Radio />}
                label="公开报名"
              />
            </RadioGroup>
          </Box>

          {rosterMode && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {ownCoachId ? '2. 选择课程' : '2. 选择教练和课程'}
              </Typography>
              <Stack spacing={2}>
                {!ownCoachId && (
                  <FormControl fullWidth size="small" disabled={coachesLoading}>
                    <InputLabel id="appointment-coach-label">教练</InputLabel>
                    <Select
                      labelId="appointment-coach-label"
                      label="教练"
                      value={coachId}
                      onChange={(e) => setCoachId(e.target.value)}
                    >
                      {coaches.map((c) => (
                        <MenuItem key={c.id} value={c.id ?? ''}>
                          {c.name ?? c.mobile ?? '未知'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {!ownCoachId && !coachesLoading && coaches.length === 0 && (
                  <Alert severity="warning">本场馆暂无教练</Alert>
                )}
                <FormControl fullWidth size="small" disabled={coursesLoading}>
                  <InputLabel id="appointment-course-label">课程</InputLabel>
                  <Select
                    labelId="appointment-course-label"
                    label="课程"
                    value={courseId}
                    onChange={(e) => selectCourse(e.target.value)}
                  >
                    {courses.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {!coursesLoading && courses.length === 0 && (
                  <Alert severity="info">暂无可预约的课程</Alert>
                )}
                {selectedCourse && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedCourse.hours} 课时 / ¥{selectedCourse.price} · 最多{' '}
                    {selectedCourse.seats} 人
                  </Typography>
                )}
              </Stack>
            </Box>
          )}

          {rosterMode === RosterMode.Invite && selectedCourse && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                3. 选择学员
              </Typography>
              {studentsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : eligibleStudents.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 1 }}>
                  暂无可预约的学员
                </Typography>
              ) : (
                <List dense disablePadding>
                  {eligibleStudents.map((student) => {
                    const selected = selectedStudentIds.includes(student.userId)
                    const headcount = headcountOf(student.userId)
                    const max = maxHeadcountOf(student.userId, student.availableHours)
                    return (
                      <ListItem
                        key={student.userId}
                        disableGutters
                        secondaryAction={
                          showHeadcount &&
                          selected &&
                          canBringCompanion(student.availableHours) && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="caption" sx={{ mr: 1 }}>
                                人数 (含本人)
                              </Typography>
                              <IconButton
                                size="small"
                                aria-label="减少人数"
                                disabled={headcount <= 1}
                                onClick={() =>
                                  setHeadcounts({
                                    ...headcounts,
                                    [student.userId]: headcount - 1,
                                  })
                                }
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <Typography sx={{ minWidth: 20, textAlign: 'center' }}>
                                {headcount}
                              </Typography>
                              <IconButton
                                size="small"
                                aria-label="增加人数"
                                disabled={headcount >= max}
                                onClick={() =>
                                  setHeadcounts({
                                    ...headcounts,
                                    [student.userId]: headcount + 1,
                                  })
                                }
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )
                        }
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selected}
                              onChange={() => toggleStudent(student.userId)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">
                                {student.name}
                                {student.mobile && ` · ${student.mobile}`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                可约 {student.availableHours} 课时 · 有效期至{' '}
                                {student.validUntil}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    )
                  })}
                </List>
              )}
              {selectedStudentIds.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  已选择 {totalHeadcount} 人 / 最多 {seats} 人
                </Typography>
              )}
            </Box>
          )}

          {rosterMode === RosterMode.Enroll && selectedCourse && (
            <Alert severity="info">
              创建后，学员可通过预约链接自行报名，报名时将自动扣除课时。最多容纳{' '}
              {selectedCourse.seats} 人。
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
        >
          创建预约
        </Button>
      </DialogActions>
    </Dialog>
  )
}
